import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";


function destinationForRole(role) {
  if (role === "talent") return "/talent";
  if (role === "creator" || role === "professional") return "/createurs";
  if (role === "admin") return "/admin";
  return "/dashboard";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, checkAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user?.role) {
      const params = new URLSearchParams(location.search);
      const nextQuery = params.get("next");
      navigate(nextQuery || destinationForRole(user.role), { replace: true });
    }
  }, [user, navigate, location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password: pwd });
      if (data.session_token) localStorage.setItem("helencia_token", data.session_token);
      setUser(data.user);
      await checkAuth();
      toast.success("Bienvenue !");
      const params = new URLSearchParams(location.search);
      const nextQuery = params.get("next");
      const next = nextQuery || location.state?.from || destinationForRole(data.user?.role);
      navigate(next, { replace: true });
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur de connexion");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell eyebrow="Connexion" title="Heureux de vous revoir." subtitle="Connectez-vous pour retrouver votre maison Helencia.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <Label className="caption-label">Email</Label>
          <div className="relative mt-2">
            <Mail className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="vous@email.com"
              className="rounded-xl pl-10 h-11"
              data-testid="login-email"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="caption-label">Mot de passe</Label>
            <Link to="/forgot-password" className="text-[10px] tracking-widest uppercase text-[#A26F3E] hover:text-[#1A1A1A]" data-testid="login-forgot-link">
              Oublié ?
            </Link>
          </div>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              type={showPwd ? "text" : "password"}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
              placeholder="••••••••"
              className="rounded-xl pl-10 pr-10 h-11"
              data-testid="login-password"
            />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#A26F3E]" tabIndex={-1}>
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {err && (
          <div className="rounded-xl bg-[#FEEFEC] border border-[#F2C0B7] text-[#A3483F] text-sm px-4 py-3" data-testid="login-error">
            {err}
          </div>
        )}

        <Button type="submit" disabled={submitting} className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="login-submit">
          {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connexion…</>) : "Se connecter"}
        </Button>
      </form>


      <p className="text-sm text-[#6E4B2A] text-center mt-8">
        Pas encore de compte ?{" "}
        <Link to="/register" className="font-semibold text-[#1A1A1A] underline" data-testid="login-register-link">
          Créer un compte
        </Link>
      </p>
    </AuthShell>
  );
}

